/**
 * HTTP client: access token giữ trong bộ nhớ (không localStorage để giảm rủi ro XSS),
 * refresh token nằm trong cookie httpOnly. Gặp 401 → tự refresh 1 lần (gộp các request đồng thời) rồi gọi lại.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(message);
  }
}

export interface SessionUser {
  id: number;
  username: string;
  fullName: string;
  roles: string[];
  /** Tên tiếng Việt của vai trò / vị trí (cùng thứ tự roles); bản API cũ chưa có. */
  roleNames?: string[];
  permissions: string[];
  departmentId: number | null;
  teamId: number | null;
  /** Phạm vi dữ liệu hiệu lực theo module (P03, ADR-018). */
  scopes: Record<string, 'own' | 'team' | 'department' | 'warehouse' | 'all'>;
  /** P21 C4: mật khẩu tạm, phải đổi trước khi dùng */
  mustChangePassword?: boolean;
}
interface SessionPayload {
  accessToken: string;
  user: SessionUser;
}

const BASE = '/api/v1';

/** Mã truy vết mỗi request (v3.1 §19): server ghi vào log và trả lại trong lỗi để đối chiếu. */
const newRequestId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
const baseHeaders = () => ({ 'X-Requested-With': 'XMLHttpRequest', 'X-Request-Id': newRequestId() });
let accessToken: string | null = null;
let refreshing: Promise<SessionPayload | null> | null = null;
let onSessionChange: (u: SessionUser | null) => void = () => undefined;
let refreshNetworkFailed = false;
/** Lần làm mới phiên gần nhất hỏng vì mất mạng (X-PWA: mở ứng dụng khi không có mạng). */
export const lastRefreshWasOffline = () => refreshNetworkFailed;

/** Access token hiện tại (cho kết nối realtime Socket.IO – P05.7). */
export const getAccessToken = () => accessToken;

export const setSessionListener = (fn: (u: SessionUser | null) => void) => {
  onSessionChange = fn;
};

function applySession(p: SessionPayload | null) {
  accessToken = p?.accessToken ?? null;
  onSessionChange(p?.user ?? null);
  return p;
}

const doRefresh = () =>
  fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include', headers: baseHeaders() }).then(async (r) => {
    if (r.ok) return applySession(await r.json());
    // Chỉ đăng xuất khi server nói phiên hết hạn/bị thu hồi. Lỗi 5xx/429 thì giữ phiên, để người dùng thử lại.
    if (r.status === 401 || r.status === 403) return applySession(null);
    return null;
  });

/**
 * Làm mới phiên. Gộp các request đồng thời trong 1 tab, và xếp hàng giữa các tab (Web Locks):
 * tab sau chờ tab trước xoay token xong rồi mới gửi, nên dùng cookie mới, không bị coi là dùng lại token cũ.
 * Mất mạng thì giữ phiên (không đá về trang đăng nhập làm mất báo giá đang soạn).
 */
export function refreshSession(): Promise<SessionPayload | null> {
  const run = async (): Promise<SessionPayload | null> =>
    typeof navigator !== 'undefined' && navigator.locks ? await navigator.locks.request('mt-refresh', doRefresh) : doRefresh();
  refreshing ??= run()
    .then((p) => {
      refreshNetworkFailed = false;
      return p;
    })
    .catch(() => {
      // fetch ném lỗi = không tới được máy chủ (mất mạng), khác với máy chủ trả 401
      refreshNetworkFailed = true;
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export async function login(username: string, password: string) {
  return applySession(await request<SessionPayload>('POST', '/auth/login', { username, password }, { auth: false }));
}

export async function logout() {
  await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'include', headers: baseHeaders() }).catch(() => undefined);
  applySession(null);
}

type Query = Record<string, string | number | boolean | undefined | null | (string | number)[]>;

export function qs(params?: Query) {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: { auth?: boolean; raw?: boolean; retry?: boolean; headers?: Record<string, string> } = {},
): Promise<T> {
  const headers: Record<string, string> = { ...baseHeaders(), ...opts.headers };
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (opts.auth !== false && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.');
  }

  if (res.status === 401 && opts.auth !== false && opts.retry !== false) {
    const s = await refreshSession();
    if (s) return request<T>(method, path, body, { ...opts, retry: false });
    // Không làm mới được nhưng phiên chưa bị thu hồi (mạng chập chờn, server bận) → báo thử lại, không đăng xuất
    if (accessToken) throw new ApiError(0, 'NETWORK', 'Mạng chập chờn, chưa làm mới được phiên đăng nhập. Vui lòng thử lại.');
  }
  if (!res.ok) {
    const j = await res.json().catch(() => null);
    throw new ApiError(res.status, j?.error?.code ?? 'HTTP', j?.error?.message ?? `Lỗi ${res.status}`, j?.error?.details, j?.requestId);
  }
  if (opts.raw) return (await res.blob()) as T;
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * POST trả luồng SSE (trợ lý AI – P05.5). Gọi onEvent cho từng sự kiện `event: x / data: {...}`.
 * Lỗi trước khi bắt đầu luồng (hạn mức, quyền…) ném ApiError như request thường. 401 → làm mới phiên 1 lần.
 */
export async function streamPost(path: string, body: unknown, onEvent: (event: string, data: unknown) => void, signal?: AbortSignal, retry = true): Promise<void> {
  const headers: Record<string, string> = { ...baseHeaders(), 'Content-Type': 'application/json', Accept: 'text/event-stream' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body), signal });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ApiError(0, 'NETWORK', 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.');
  }
  if (res.status === 401 && retry) {
    const s = await refreshSession();
    if (s) return streamPost(path, body, onEvent, signal, false);
  }
  if (!res.ok || !res.body || !res.headers.get('content-type')?.startsWith('text/event-stream')) {
    const j = await res.json().catch(() => null);
    throw new ApiError(res.status, j?.error?.code ?? 'HTTP', j?.error?.message ?? `Lỗi ${res.status}`, j?.error?.details, j?.requestId);
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const ev = /^event: (.+)$/m.exec(chunk)?.[1];
      const data = /^data: (.+)$/m.exec(chunk)?.[1];
      if (ev && data) onEvent(ev, JSON.parse(data));
    }
  }
}

export const api = {
  get: <T>(path: string, params?: Query) => request<T>('GET', path + qs(params)),
  /** headers: thêm header riêng (VD: Idempotency-Key cho thao tác không được chạy 2 lần). */
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>('POST', path, body ?? {}, { headers }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T = void>(path: string) => request<T>('DELETE', path),
  blob: (path: string, params?: Query) => request<Blob>('GET', path + qs(params), undefined, { raw: true }),
  /** POST nhận file (VD: giọng đọc AI mp3) */
  postBlob: (path: string, body: unknown) => request<Blob>('POST', path, body, { raw: true }),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, form),
};
